import React, { useState, useEffect, useCallback } from 'react';
import {
  Globe,
  RefreshCw,
  Play,
  Square,
  Upload,
  Eye,
  ExternalLink,
  Send,
  Check,
  X,
  AlertCircle,
  FileText,
  Loader2,
  Image as ImageIcon,
  Settings,
  Link,
  Copy,
  ClipboardCheck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NinthwareTab = ({ token }) => {
  // Configuration State
  const [config, setConfig] = useState({
    configured: false,
    email: '',
    browser_active: false,
    is_logged_in: false
  });
  const [configEmail, setConfigEmail] = useState('');
  const [configPassword, setConfigPassword] = useState('');
  const [configModalOpen, setConfigModalOpen] = useState(false);
  
  // Browser State
  const [browserScreenshot, setBrowserScreenshot] = useState(null);
  const [browserLoading, setBrowserLoading] = useState(false);
  const [loginStep, setLoginStep] = useState('idle'); // idle, started, captcha, logged_in
  
  // Applications State
  const [applications, setApplications] = useState([]);
  const [appFilter, setAppFilter] = useState({ type: 'both', status: 'all' });
  const [loadingApps, setLoadingApps] = useState(false);
  
  // Tracking State
  const [trackingRecords, setTrackingRecords] = useState([]);
  const [loadingTracking, setLoadingTracking] = useState(false);
  
  // Push State
  const [pushingApp, setPushingApp] = useState(null);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  
  // Preview State
  const [previewApp, setPreviewApp] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  
  // Upload Print State
  const [uploadPrintApp, setUploadPrintApp] = useState(null);
  const [uploadPrintFile, setUploadPrintFile] = useState(null);
  const [uploadPrintModalOpen, setUploadPrintModalOpen] = useState(false);
  
  // Update Ref State
  const [updateRefApp, setUpdateRefApp] = useState(null);
  const [updateRefValue, setUpdateRefValue] = useState('');
  const [updateRefModalOpen, setUpdateRefModalOpen] = useState(false);
  
  // OTP State
  const [needsOtp, setNeedsOtp] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  
  // CAPTCHA State
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  // Fetch configuration
  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/ninthware/config`, { headers });
      setConfig(res.data);
      if (res.data.email) {
        setConfigEmail(res.data.email);
      }
    } catch (err) {
      console.error('Failed to fetch config:', err);
    }
  }, [token]);

  // Fetch applications
  const fetchApplications = useCallback(async () => {
    setLoadingApps(true);
    try {
      const res = await axios.get(`${API}/admin/ninthware/applications`, {
        headers,
        params: { app_type: appFilter.type, status: appFilter.status }
      });
      setApplications(res.data);
    } catch (err) {
      console.error('Failed to fetch applications:', err);
      toast.error('Failed to load applications');
    } finally {
      setLoadingApps(false);
    }
  }, [token, appFilter]);

  // Fetch tracking records
  const fetchTracking = useCallback(async () => {
    setLoadingTracking(true);
    try {
      const res = await axios.get(`${API}/admin/ninthware/tracking`, { headers });
      setTrackingRecords(res.data);
    } catch (err) {
      console.error('Failed to fetch tracking:', err);
    } finally {
      setLoadingTracking(false);
    }
  }, [token]);

  // Fetch browser status
  const fetchBrowserStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/ninthware/browser/status`, { headers });
      setConfig(prev => ({
        ...prev,
        browser_active: res.data.browser_active,
        is_logged_in: res.data.is_logged_in
      }));
      if (res.data.screenshot) {
        setBrowserScreenshot(res.data.screenshot);
      }
      if (res.data.is_logged_in) {
        setLoginStep('logged_in');
      }
    } catch (err) {
      console.error('Failed to fetch browser status:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchConfig();
    fetchApplications();
    fetchTracking();
  }, [fetchConfig, fetchApplications, fetchTracking]);

  // Auto-refresh browser status when active
  useEffect(() => {
    if (config.browser_active) {
      const interval = setInterval(fetchBrowserStatus, 10000);
      return () => clearInterval(interval);
    }
  }, [config.browser_active, fetchBrowserStatus]);

  // Save configuration
  const saveConfig = async () => {
    try {
      const formData = new FormData();
      formData.append('email', configEmail);
      formData.append('password', configPassword);
      
      await axios.post(`${API}/admin/ninthware/config`, formData, { headers });
      toast.success('Configuration saved');
      setConfigModalOpen(false);
      fetchConfig();
    } catch (err) {
      toast.error('Failed to save configuration');
    }
  };

  // Browser controls
  const startBrowser = async () => {
    setBrowserLoading(true);
    try {
      await axios.post(`${API}/admin/ninthware/browser/start`, {}, { headers });
      toast.success('Browser started');
      fetchBrowserStatus();
    } catch (err) {
      toast.error('Failed to start browser');
    } finally {
      setBrowserLoading(false);
    }
  };

  const stopBrowser = async () => {
    setBrowserLoading(true);
    try {
      await axios.post(`${API}/admin/ninthware/browser/stop`, {}, { headers });
      toast.success('Browser stopped');
      setBrowserScreenshot(null);
      setLoginStep('idle');
      setConfig(prev => ({ ...prev, browser_active: false, is_logged_in: false }));
    } catch (err) {
      toast.error('Failed to stop browser');
    } finally {
      setBrowserLoading(false);
    }
  };

  // Login flow
  const startLogin = async () => {
    setBrowserLoading(true);
    setLoginStep('started');
    try {
      const res = await axios.post(`${API}/admin/ninthware/login/start`, {}, { headers });
      if (res.data.screenshot) {
        setBrowserScreenshot(res.data.screenshot);
      }
      if (res.data.success) {
        setLoginStep('captcha');
        toast.info('Credentials filled - CAPTCHA needs solving');
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error('Failed to start login');
      setLoginStep('idle');
    } finally {
      setBrowserLoading(false);
    }
  };

  // CAPTCHA handling
  const fillCaptcha = async () => {
    if (!captchaCode) {
      toast.error('Please enter the CAPTCHA code');
      return;
    }
    
    setCaptchaLoading(true);
    try {
      const formData = new FormData();
      formData.append('captcha', captchaCode);
      
      const res = await axios.post(`${API}/admin/ninthware/login/fill-captcha`, formData, { headers });
      if (res.data.screenshot) {
        setBrowserScreenshot(res.data.screenshot);
      }
      if (res.data.success) {
        toast.success('CAPTCHA filled - now click "Click Login"');
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error('Failed to fill CAPTCHA');
    } finally {
      setCaptchaLoading(false);
    }
  };

  const clickLogin = async () => {
    setBrowserLoading(true);
    try {
      const res = await axios.post(`${API}/admin/ninthware/login/click`, {}, { headers });
      if (res.data.screenshot) {
        setBrowserScreenshot(res.data.screenshot);
      }
      
      // Check if OTP is needed
      if (res.data.needs_otp) {
        setNeedsOtp(true);
        setLoginStep('otp');
        toast.info('OTP verification required - check your email');
      } else if (res.data.success) {
        setLoginStep('logged_in');
        setNeedsOtp(false);
        setConfig(prev => ({ ...prev, is_logged_in: true }));
        toast.success('Successfully logged in to Ninthware');
      } else {
        toast.warning(res.data.message);
      }
    } catch (err) {
      toast.error('Login click failed');
    } finally {
      setBrowserLoading(false);
    }
  };

  // OTP handling
  const fillOtp = async () => {
    if (!otpCode || otpCode.length < 4) {
      toast.error('Please enter a valid OTP code');
      return;
    }
    
    setOtpLoading(true);
    try {
      const formData = new FormData();
      formData.append('otp', otpCode);
      
      const res = await axios.post(`${API}/admin/ninthware/login/fill-otp`, formData, { headers });
      if (res.data.screenshot) {
        setBrowserScreenshot(res.data.screenshot);
      }
      if (res.data.success) {
        toast.success('OTP entered - click Submit OTP to verify');
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error('Failed to fill OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const submitOtp = async () => {
    setOtpLoading(true);
    try {
      const res = await axios.post(`${API}/admin/ninthware/login/submit-otp`, {}, { headers });
      if (res.data.screenshot) {
        setBrowserScreenshot(res.data.screenshot);
      }
      if (res.data.success) {
        setLoginStep('logged_in');
        setNeedsOtp(false);
        setOtpCode('');
        setConfig(prev => ({ ...prev, is_logged_in: true }));
        toast.success('OTP verified - Successfully logged in!');
      } else {
        toast.error(res.data.message || 'OTP verification failed');
      }
    } catch (err) {
      toast.error('OTP submission failed');
    } finally {
      setOtpLoading(false);
    }
  };

  const checkLoginStatus = async () => {
    setBrowserLoading(true);
    try {
      const res = await axios.get(`${API}/admin/ninthware/login/check`, { headers });
      if (res.data.screenshot) {
        setBrowserScreenshot(res.data.screenshot);
      }
      if (res.data.is_logged_in) {
        setLoginStep('logged_in');
        setConfig(prev => ({ ...prev, is_logged_in: true }));
        toast.success('Login confirmed');
      } else {
        toast.info(res.data.message);
      }
    } catch (err) {
      toast.error('Failed to check login status');
    } finally {
      setBrowserLoading(false);
    }
  };

  // Preview application mapping
  const openPreview = async (app) => {
    setPreviewApp(app);
    try {
      const res = await axios.get(`${API}/admin/ninthware/preview/${app.id}`, {
        headers,
        params: { application_type: app.app_type }
      });
      setPreviewData(res.data.mapped_for_ninthware);
      setPreviewModalOpen(true);
    } catch (err) {
      toast.error('Failed to load preview');
    }
  };

  // Push application to Ninthware
  const pushApplication = async (app) => {
    if (!config.is_logged_in) {
      toast.error('Please log in to Ninthware first');
      return;
    }
    
    setPushLoading(true);
    setPushingApp(app);
    setPushResult(null);
    
    try {
      const formData = new FormData();
      formData.append('application_type', app.app_type);
      
      const res = await axios.post(`${API}/admin/ninthware/push/${app.id}`, formData, { headers });
      setPushResult(res.data);
      if (res.data.screenshot) {
        setBrowserScreenshot(res.data.screenshot);
      }
      if (res.data.success) {
        toast.success('Application form filled in Ninthware');
        fetchTracking();
        fetchApplications();
      } else {
        toast.warning(res.data.message);
      }
    } catch (err) {
      toast.error('Failed to push application');
      setPushResult({ success: false, message: err.response?.data?.detail || 'Push failed' });
    } finally {
      setPushLoading(false);
    }
  };

  // Upload print document
  const uploadPrint = async () => {
    if (!uploadPrintFile || !uploadPrintApp) return;
    
    try {
      const formData = new FormData();
      formData.append('print_file', uploadPrintFile);
      
      await axios.post(`${API}/admin/ninthware/tracking/${uploadPrintApp.application_id}/upload-print`, formData, { headers });
      toast.success('Print uploaded successfully');
      setUploadPrintModalOpen(false);
      setUploadPrintFile(null);
      fetchTracking();
    } catch (err) {
      toast.error('Failed to upload print');
    }
  };

  // Update Ninthware reference
  const updateRef = async () => {
    if (!updateRefValue || !updateRefApp) return;
    
    try {
      const formData = new FormData();
      formData.append('ninthware_ref', updateRefValue);
      
      await axios.post(`${API}/admin/ninthware/tracking/${updateRefApp.application_id}/update-ref`, formData, { headers });
      toast.success('Reference updated');
      setUpdateRefModalOpen(false);
      setUpdateRefValue('');
      fetchTracking();
    } catch (err) {
      toast.error('Failed to update reference');
    }
  };

  // Delete tracking record
  const deleteTracking = async (applicationId) => {
    if (!window.confirm('Delete tracking record? This allows re-pushing the application.')) return;
    
    try {
      await axios.delete(`${API}/admin/ninthware/tracking/${applicationId}`, { headers });
      toast.success('Tracking record deleted');
      fetchTracking();
      fetchApplications();
    } catch (err) {
      toast.error('Failed to delete tracking');
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'form_filled': 'bg-blue-100 text-blue-800',
      'submitted': 'bg-green-100 text-green-800',
      'form_error': 'bg-red-100 text-red-800',
      'submit_error': 'bg-red-100 text-red-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Configuration & Browser Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Configuration Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Ninthware Configuration
            </CardTitle>
            <CardDescription>
              Configure Ninthware portal credentials
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Status:</span>
              <Badge variant={config.configured ? "default" : "secondary"}>
                {config.configured ? 'Configured' : 'Not Configured'}
              </Badge>
            </div>
            {config.configured && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Email:</span>
                <span className="text-sm font-medium">{config.email}</span>
              </div>
            )}
            <Button onClick={() => setConfigModalOpen(true)} className="w-full">
              <Settings className="w-4 h-4 mr-2" />
              {config.configured ? 'Update Credentials' : 'Configure Credentials'}
            </Button>
          </CardContent>
        </Card>

        {/* Quick Access Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ExternalLink className="w-5 h-5" />
              Quick Access
            </CardTitle>
            <CardDescription>
              Open Ninthware portal to manually enter application data
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-blue-800 text-sm">
                <strong>Recommended workflow:</strong>
              </p>
              <ol className="text-blue-700 text-sm mt-2 space-y-1 list-decimal list-inside">
                <li>Click "Open Ninthware" below</li>
                <li>Log in with your credentials</li>
                <li>Select an application from the table</li>
                <li>Click the copy icon to export data</li>
                <li>Paste each field into Ninthware</li>
              </ol>
            </div>
            <Button 
              onClick={() => window.open('https://b2b.globalguidetour.com/', '_blank')}
              className="w-full"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open Ninthware Portal
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Optional: Browser Automation (Advanced) */}
      <details className="group">
        <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700 flex items-center gap-2 mb-4">
          <Settings className="w-4 h-4" />
          Advanced: Browser Automation (Beta)
        </summary>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Browser Control Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Globe className="w-4 h-4" />
              Browser Session
            </CardTitle>
            <CardDescription className="text-xs">
              Automated browser for Ninthware (experimental)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Browser:</span>
              <Badge variant={config.browser_active ? "default" : "secondary"} className="text-xs">
                {config.browser_active ? 'Active' : 'Stopped'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Login Status:</span>
              <Badge variant={config.is_logged_in ? "default" : "destructive"} className={`text-xs ${config.is_logged_in ? "bg-green-500" : ""}`}>
                {config.is_logged_in ? 'Logged In' : 'Not Logged In'}
              </Badge>
            </div>
            <div className="flex gap-2">
              {!config.browser_active ? (
                <Button onClick={startBrowser} disabled={browserLoading || !config.configured} size="sm" className="flex-1">
                  {browserLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}
                  Start
                </Button>
              ) : (
                <Button onClick={stopBrowser} variant="destructive" disabled={browserLoading} size="sm" className="flex-1">
                  {browserLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Square className="w-3 h-3 mr-1" />}
                  Stop
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
        </div>
      </details>

      {/* Login Flow - only show when browser is active */}
      {config.browser_active && !config.is_logged_in && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ExternalLink className="w-5 h-5" />
              Login to Ninthware
            </CardTitle>
            <CardDescription>
              Semi-automated login - CAPTCHA and OTP must be handled manually
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <Badge variant="outline" className={loginStep === 'started' ? 'bg-blue-50' : ''}>1. Fill Credentials</Badge>
              <span className="text-gray-400">→</span>
              <Badge variant="outline" className={loginStep === 'captcha' ? 'bg-yellow-50' : ''}>2. Solve CAPTCHA</Badge>
              <span className="text-gray-400">→</span>
              <Badge variant="outline" className={loginStep === 'otp' ? 'bg-purple-50' : ''}>3. Enter OTP</Badge>
              <span className="text-gray-400">→</span>
              <Badge variant="outline" className={loginStep === 'logged_in' ? 'bg-green-50' : ''}>4. Logged In</Badge>
            </div>
            
            <div className="flex gap-2 flex-wrap">
              <Button 
                onClick={startLogin} 
                disabled={browserLoading || loginStep === 'logged_in' || loginStep === 'otp'}
                variant={loginStep === 'idle' ? 'default' : 'outline'}
              >
                {browserLoading && loginStep === 'started' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Step 1: Fill Credentials
              </Button>
              <Button 
                onClick={clickLogin} 
                disabled={browserLoading || (loginStep !== 'captcha' && loginStep !== 'started')}
                variant={loginStep === 'captcha' ? 'default' : 'outline'}
              >
                {browserLoading && loginStep === 'captcha' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Step 2: Click Login
              </Button>
              <Button 
                onClick={checkLoginStatus} 
                disabled={browserLoading}
                variant="outline"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Check Status
              </Button>
            </div>
            
            {loginStep === 'captcha' && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg space-y-3">
                <p className="text-yellow-800 font-medium flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  CAPTCHA Required
                </p>
                <p className="text-yellow-700 text-sm">
                  Look at the CAPTCHA image in the screenshot below and enter the text:
                </p>
                <div className="flex gap-2 flex-wrap">
                  <Input 
                    type="text"
                    value={captchaCode}
                    onChange={(e) => setCaptchaCode(e.target.value)}
                    placeholder="Enter CAPTCHA text"
                    className="max-w-[200px]"
                  />
                  <Button 
                    onClick={fillCaptcha}
                    disabled={captchaLoading || !captchaCode}
                    variant="outline"
                  >
                    {captchaLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Fill CAPTCHA
                  </Button>
                  <Button 
                    onClick={clickLogin} 
                    disabled={browserLoading}
                  >
                    {browserLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Click Login
                  </Button>
                </div>
              </div>
            )}
            
            {/* OTP Entry Section */}
            {(loginStep === 'otp' || needsOtp) && (
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg space-y-3">
                <p className="text-purple-800 font-medium flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  OTP Verification Required
                </p>
                <p className="text-purple-700 text-sm">
                  An OTP has been sent to the registered email. Enter it below:
                </p>
                <div className="flex gap-2">
                  <Input 
                    type="text"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    placeholder="Enter OTP code"
                    className="max-w-[200px]"
                    maxLength={10}
                  />
                  <Button 
                    onClick={fillOtp}
                    disabled={otpLoading || !otpCode}
                    variant="outline"
                  >
                    {otpLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Fill OTP
                  </Button>
                  <Button 
                    onClick={submitOtp}
                    disabled={otpLoading}
                  >
                    {otpLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Submit OTP
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Browser Screenshot */}
      {browserScreenshot && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5" />
              Browser View
            </CardTitle>
            <CardDescription>
              Current state of the Ninthware browser window
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden">
              <img 
                src={`data:image/png;base64,${browserScreenshot}`} 
                alt="Browser Screenshot" 
                className="w-full"
              />
            </div>
            <Button onClick={fetchBrowserStatus} variant="outline" className="mt-4">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh Screenshot
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Applications to Push */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Applications
              </CardTitle>
              <CardDescription>
                Select applications to push to Ninthware
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Select value={appFilter.type} onValueChange={(v) => setAppFilter(prev => ({ ...prev, type: v }))}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">All Types</SelectItem>
                  <SelectItem value="b2c">B2C Only</SelectItem>
                  <SelectItem value="b2b">B2B Only</SelectItem>
                </SelectContent>
              </Select>
              <Select value={appFilter.status} onValueChange={(v) => setAppFilter(prev => ({ ...prev, status: v }))}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={fetchApplications} variant="outline" size="icon">
                <RefreshCw className={`w-4 h-4 ${loadingApps ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Applicant</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Nationality</TableHead>
                  <TableHead>Visa Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Pushed</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {applications.slice(0, 50).map((app) => (
                  <TableRow key={app.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{app.first_name} {app.last_name}</p>
                        <p className="text-sm text-gray-500">{app.passport_number}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{app.app_type.toUpperCase()}</Badge>
                    </TableCell>
                    <TableCell>{app.nationality}</TableCell>
                    <TableCell>{app.visa_duration || app.visa_type || '30 Days'}</TableCell>
                    <TableCell>
                      <Badge>{app.status}</Badge>
                    </TableCell>
                    <TableCell>
                      {app.already_pushed ? (
                        <Badge className="bg-green-100 text-green-800">
                          <Check className="w-3 h-3 mr-1" /> Pushed
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Not Pushed</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => openPreview(app)}
                          title="Copy Data"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        {config.is_logged_in && (
                          <Button 
                            size="sm" 
                            onClick={() => pushApplication(app)}
                            disabled={pushLoading}
                            title="Auto-fill & Submit to Ninthware"
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {pushLoading && pushingApp?.id === app.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {applications.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      No applications found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {/* Show message when not logged in */}
          {!config.is_logged_in && (
            <p className="text-sm text-amber-600 mt-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Log in to Ninthware (via Advanced section) to enable auto-push feature
            </p>
          )}
        </CardContent>
      </Card>

      {/* Tracking Records */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Link className="w-5 h-5" />
                Ninthware Tracking
              </CardTitle>
              <CardDescription>
                Track applications pushed to Ninthware and their references
              </CardDescription>
            </div>
            <Button onClick={fetchTracking} variant="outline" size="icon">
              <RefreshCw className={`w-4 h-4 ${loadingTracking ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Applicant</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Ninthware Ref</TableHead>
                  <TableHead>Pushed At</TableHead>
                  <TableHead>Print</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trackingRecords.map((record) => (
                  <TableRow key={record.application_id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{record.applicant_name}</p>
                        <p className="text-sm text-gray-500">{record.nationality}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{record.application_type?.toUpperCase()}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusBadge(record.status)}>
                        {record.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {record.ninthware_ref ? (
                        <span className="font-mono text-sm">{record.ninthware_ref}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {record.pushed_at ? new Date(record.pushed_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      {record.ninthware_print_url ? (
                        <a 
                          href={`${BACKEND_URL}${record.ninthware_print_url}`} 
                          target="_blank" 
                          rel="noreferrer"
                          className="text-blue-600 hover:underline flex items-center gap-1"
                        >
                          <FileText className="w-4 h-4" />
                          View
                        </a>
                      ) : (
                        <span className="text-gray-400">No print</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setUpdateRefApp(record);
                            setUpdateRefValue(record.ninthware_ref || '');
                            setUpdateRefModalOpen(true);
                          }}
                          title="Update Reference"
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setUploadPrintApp(record);
                            setUploadPrintModalOpen(true);
                          }}
                          title="Upload Print"
                        >
                          <Upload className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => deleteTracking(record.application_id)}
                          title="Delete & Allow Re-push"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {trackingRecords.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      No tracking records yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Push Result Card */}
      {pushResult && (
        <Card className={pushResult.success ? 'border-green-200' : 'border-red-200'}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {pushResult.success ? (
                <Check className="w-5 h-5 text-green-600" />
              ) : (
                <X className="w-5 h-5 text-red-600" />
              )}
              Push Result
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className={pushResult.success ? 'text-green-700' : 'text-red-700'}>
              {pushResult.message}
            </p>
            {pushResult.ninthware_ref && (
              <p className="mt-2">
                <strong>Ninthware Reference:</strong> <span className="font-mono">{pushResult.ninthware_ref}</span>
              </p>
            )}
            {pushResult.next_action && (
              <p className="mt-2 text-sm text-gray-600">
                <strong>Next:</strong> {pushResult.next_action}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Configuration Modal */}
      <Dialog open={configModalOpen} onOpenChange={setConfigModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ninthware Configuration</DialogTitle>
            <DialogDescription>
              Enter your Ninthware portal credentials
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input 
                type="email"
                value={configEmail}
                onChange={(e) => setConfigEmail(e.target.value)}
                placeholder="your-email@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input 
                type="password"
                value={configPassword}
                onChange={(e) => setConfigPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigModalOpen(false)}>Cancel</Button>
            <Button onClick={saveConfig} disabled={!configEmail || !configPassword}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Modal - Data Export with Copy Buttons */}
      <Dialog open={previewModalOpen} onOpenChange={setPreviewModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="w-5 h-5" />
              Export Data for Ninthware
            </DialogTitle>
            <DialogDescription>
              Click the copy button next to each field to copy the value, then paste it into Ninthware
            </DialogDescription>
          </DialogHeader>
          {previewData && previewApp && (
            <div className="space-y-4 py-4">
              {/* Applicant Info Header */}
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="font-semibold text-blue-800">
                  {previewApp.first_name} {previewApp.last_name} - {previewApp.passport_number}
                </p>
                <p className="text-sm text-blue-600">{previewApp.nationality} | {previewApp.visa_duration || '30 Days'}</p>
              </div>
              
              {/* Copy All Button */}
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    const allData = Object.entries(previewData)
                      .map(([key, value]) => `${key.replace(/_/g, ' ').toUpperCase()}: ${value || '-'}`)
                      .join('\n');
                    navigator.clipboard.writeText(allData);
                    toast.success('All data copied to clipboard!');
                  }}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy All Fields
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    window.open('https://b2b.globalguidetour.com/', '_blank');
                  }}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open Ninthware
                </Button>
                {config.is_logged_in && (
                  <Button
                    onClick={() => {
                      setPreviewModalOpen(false);
                      pushApplication(previewApp);
                    }}
                    disabled={pushLoading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {pushLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Auto Push to Ninthware
                  </Button>
                )}
              </div>

              {/* Fields Grid with Copy Buttons */}
              <div className="border rounded-lg divide-y">
                {/* Ninthware Field Order - matching their form */}
                {[
                  { key: 'visa_type', label: 'Visa Type' },
                  { key: 'passport_nationality', label: 'Passport Nationality' },
                  { key: 'passport_number', label: 'Passport Number' },
                  { key: 'first_name', label: 'First Name' },
                  { key: 'last_name', label: 'Last Name' },
                  { key: 'gender', label: 'Gender' },
                  { key: 'date_of_birth', label: 'Date of Birth' },
                  { key: 'place_of_birth', label: 'Place of Birth' },
                  { key: 'passport_issue_date', label: 'Passport Issue Date' },
                  { key: 'passport_expiry_date', label: 'Passport Expiry Date' },
                  { key: 'father_name', label: 'Father Name' },
                  { key: 'mother_name', label: 'Mother Name' },
                  { key: 'marital_status', label: 'Marital Status' },
                  { key: 'profession', label: 'Profession' },
                  { key: 'religion', label: 'Religion' },
                  { key: 'nationality', label: 'Nationality' },
                ].map(({ key, label }) => {
                  const value = previewData[key] || '-';
                  return (
                    <div key={key} className="flex items-center justify-between p-3 hover:bg-gray-50">
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 uppercase">{label}</p>
                        <p className="font-medium">{value}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          navigator.clipboard.writeText(value);
                          toast.success(`Copied: ${label}`);
                        }}
                        className="ml-2"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>

              {/* Instructions */}
              <div className="bg-yellow-50 p-4 rounded-lg">
                <p className="text-yellow-800 font-medium flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  How to use:
                </p>
                <ol className="text-yellow-700 text-sm mt-2 space-y-1 list-decimal list-inside">
                  <li>Click "Open Ninthware" to open the portal in a new tab</li>
                  <li>Log in to Ninthware manually</li>
                  <li>Navigate to Dubai Application form</li>
                  <li>Click the copy button next to each field here</li>
                  <li>Paste the value into the corresponding Ninthware field</li>
                  <li>After submission, come back and update the Ninthware reference</li>
                </ol>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewModalOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Print Modal */}
      <Dialog open={uploadPrintModalOpen} onOpenChange={setUploadPrintModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Ninthware Print</DialogTitle>
            <DialogDescription>
              Upload the print/confirmation document from Ninthware for this application
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Print Document</Label>
              <Input 
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={(e) => setUploadPrintFile(e.target.files?.[0] || null)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setUploadPrintModalOpen(false);
              setUploadPrintFile(null);
            }}>Cancel</Button>
            <Button onClick={uploadPrint} disabled={!uploadPrintFile}>
              <Upload className="w-4 h-4 mr-2" />
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Reference Modal */}
      <Dialog open={updateRefModalOpen} onOpenChange={setUpdateRefModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Ninthware Reference</DialogTitle>
            <DialogDescription>
              Manually update the Ninthware reference number for tracking
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Ninthware Reference</Label>
              <Input 
                value={updateRefValue}
                onChange={(e) => setUpdateRefValue(e.target.value)}
                placeholder="e.g., NW123456"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setUpdateRefModalOpen(false);
              setUpdateRefValue('');
            }}>Cancel</Button>
            <Button onClick={updateRef} disabled={!updateRefValue}>
              Save Reference
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NinthwareTab;
